/**
 * Consider making it a plugin while doing rewrite so a user doesn't have to import it every time it wans to use it.
 */

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import randomString from '@/helpers/randomString';
import Countdown from '@/components/common/Countdown';

/**
 * Toaster mixin for displaying different kinds of Toaster messages.
 */
export default {
  components: {
    SBIcon,
    Countdown,
  },
  data() {
    return {
      SBColors,
      SBIconList,
      shownSystemMessages: [],
    };
  },
  computed: {
    version() {
      return process.env.VUE_APP_VERSION;
    },
  },
  methods: {
    // LocalStorage variable name ==> readSystemMessages
    addSystemMessageIdToLocalStorage(id) {
      if (id && !this.checkSystemMessageIdInLocalStorage(id)) {
        const IDs = JSON.parse(localStorage.getItem('readSystemMessages'));
        IDs.push(id);
        localStorage.setItem('readSystemMessages', JSON.stringify(IDs));
      }
    },
    // TODO this and all similar methods should be moved to a specific mixin.
    /**
     * Method filters out old messages from the localStorage keeping only those that are being received and acknowledged by user. This prevents localstorage from filling up with old IDs that are not relevant any more.
     * @param {Number[]} messagesIdList
     */
    removeSystemMessageIdFromLocalStorage(messagesIdList) {
      const IDs = JSON.parse(localStorage.getItem('readSystemMessages'));
      if (IDs) {
        const newIDs = IDs.filter(id => messagesIdList.includes(id));
        localStorage.setItem('readSystemMessages', JSON.stringify(newIDs));
      }
    },
    checkSystemMessageIdInLocalStorage(id) {
      let result = false;
      let IDs = JSON.parse(localStorage.getItem('readSystemMessages'));
      if (!IDs || (IDs && !Array.isArray(IDs))) {
        IDs = [];
        localStorage.setItem('readSystemMessages', JSON.stringify(IDs));
      }
      if (IDs.includes(id)) {
        result = true;
      }
      return result;
    },
    addShownSystemMessageId(id) {
      if (!this.checkShownSystemMessageId(id)) {
        this.shownSystemMessages.push(id);
      }
    },
    removeShownSystemMessageId(id) {
      const removeMessageIndex = this.shownSystemMessages.findIndex(messageId => messageId === id);
      this.shownSystemMessages.splice(removeMessageIndex, 1);
    },
    checkShownSystemMessageId(id) {
      let result = false;
      if (this.shownSystemMessages.includes(id)) {
        result = true;
      }
      return result;
    },
    /**
     * Basic Toaster message with icon, text and close button.
     * Header title is hidden.
     *
     * @param {*} text Text to show in a Toaster
     * @param {*} icon Optional. Leading Icon to show in a Toaster
     */
    showToaster(text, icon = null, variant = null, emojiIcon = null) {
      const id = randomString();
      const iconColor = variant === 'danger' ? '#ff8686' : (variant === 'warning' ? '#fff9b0' : this.SBColors.white);

      if (emojiIcon === 'warning') {
        text = `<span class="emoji-fix">⚠️</span>${text}`;
      } else if (emojiIcon === 'error') {
        text = `<span class="emoji-fix">‼️</span>${text}`;
      } else if (emojiIcon === 'OK') {
        text = `<span class="emoji-fix">👍</span>${text}`;
      }

      const h = this.$createElement;
      const vNodesMsg = h(
        'div',
        { class: 'flex flex-align-center' },
        [
          icon ? h(
            'SBIcon',
            {
              props: {
                icon,
                color: iconColor,
                size: 16,
              },
            },
          ) : null,
          h('p', { class: 'greedy', domProps: { innerHTML: text } }),
          h(
            'SBIcon',
            {
              class: 'close-btn',
              props: {
                icon: this.SBIconList.CLOSE_THIN,
                color: '#8E9BA2',
                borderRadius: 100,
                hoverSize: 25,
                hoverIconColor: this.SBColors.greyUltralight,
                size: 10,
              },
              on: {
                click: () => this.$bvToast.hide(id),
              },
            },
            'Close',
          ),
        ],
      );

      this.$bvToast.toast(vNodesMsg, {
        id,
        title: 'Toaster',
        toaster: 'b-toaster-bottom-left',
        toastClass: 'sb-toast hide-header',
        solid: true,
        // noAutoHide: true, // usefull for development
        appendToast: true,
      });
    },
    /**
     * @param {*} message
     * @param {*} icon
     * @param {*} variant
     * @param {*} button
     */
    showSystemMessage(message, icon = null, variant = null, button = null, emoji = null) {
      // Only show the Toast message if user haven't seen it and interacted with it or if restart message publish time is after the app build time.
      if (
        this.checkSystemMessageIdInLocalStorage(message.id) ||
        this.checkShownSystemMessageId(message.id) ||
        (message.action === 'restart' && process.env.VUE_APP_BUILD_TIME && this.$moment(false, false).unix(process.env.VUE_APP_BUILD_TIME).diff(this.$moment(false, false).unix(message.valid_from)) > 0)
      ) {
        return;
      }

      this.addShownSystemMessageId(message.id);

      const iconColor = variant === 'danger' ? '#ff8686' : (variant === 'warning' ? '#fff9b0' : this.SBColors.white);

      // System message callback
      //   - Closes the toast.
      //   - Opens url in new tab if needed.
      //   - Adds id to localStorage so the message doesn't show up any more.
      const callback = (url = null) => {
        if (message.action === 'restart') {
          window.location.replace(`${window.location.href}?v=${this.version}`);
          // window.location.reload();
        } else if (url) {
          window.open(url, '_blank').focus();
        }
        this.$bvToast.hide(`system-message-${message.id}`);
        this.addSystemMessageIdToLocalStorage(message.id);
        this.removeShownSystemMessageId(message.id);
      };

      const h = this.$createElement;
      const vNodesMsg = h(
        'div',
        { class: 'flex flex-align-center' },
        [
          // ICON
          icon ? h(
            'SBIcon',
            {
              style: 'line-height: 1',
              props: {
                icon,
                color: iconColor,
                size: 16,
              },
            },
          ) : emoji ? h('p', { class: 'mr-3', domProps: { innerHTML: `${emoji}` } }) : null,
          h(
            'div',
            { class: '', style: 'line-height: 1.2' },
            [
              // TEXT
              h('span', { class: 'greedy bold', domProps: { innerHTML: message.message } }),
              message.auto_action_until_seconds ?
                [
                  h('span', { class: 'mx-1', domProps: { innerText: message.action === 'restart' ? 'Your page will reload in' : '' } }),
                  this.$createElement(Countdown, { attrs: { color: '#F5BFC1', seconds: message.auto_action_until_seconds, callback } }),
                ] :
                null,
            ],
          ),
          // BUTTON
          button ?
            h(
              'b-button',
              {
                class: 'greedy bold glass',
                domProps: {
                  innerHTML: button.text,
                },
                on: {
                  click: () => callback(button.url),
                },
              },
            ) :
            h(
              'SBIcon',
              {
                class: 'close-btn',
                props: {
                  icon: this.SBIconList.CLOSE_THIN,
                  color: '#8E9BA2',
                  borderRadius: 100,
                  hoverSize: 25,
                  hoverIconColor: this.SBColors.greyUltralight,
                  size: 10,
                },
                on: {
                  click: () => callback(),
                },
              },
              'Close',
            ),
        ],
      );

      this.$bvToast.toast(vNodesMsg, {
        id: `system-message-${message.id}`,
        title: 'Toaster',
        toaster: 'b-toaster-bottom-center',
        toastClass: 'sb-toast hide-header',
        solid: true,
        noAutoHide: true, // System messages must be visible until user interacts.
        appendToast: true,
      });
    },
  },
};
